import _decorate from "@babel/runtime/helpers/esm/decorate";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime/helpers/esm/get";
let _ = t => t,
  _t,
  _t2;
function _superPropGet(t, e, r, o) { var p = _get(_getPrototypeOf(1 & o ? t.prototype : t), e, r); return 2 & o ? function (t) { return p.apply(r, t); } : p; }
/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import C4DLinkWithIcon from "../link-with-icon/link-with-icon.js";
import Error20 from '@carbon/web-components/es/icons/error/20.js';
import styles from "././card.css.js";
const {
  prefix,
  stablePrefix: c4dPrefix
} = settings;

/**
 * Card footer.
 *
 * @element c4d-card-footer
 * @csspart copy - The Copy. Usage: `c4d-card-footer::part(copy)`
 */
let C4DCardFooter = _decorate([customElement(`${c4dPrefix}-card-footer`)], function (_initialize, _C4DLinkWithIcon) {
  class C4DCardFooter extends _C4DLinkWithIcon {
    constructor(...args) {
      super(...args);
      _initialize(this);
    }
  }
  return {
    F: C4DCardFooter,
    d: [{
      kind: "field",
      decorators: [query(`.${c4dPrefix}-ce--card__footer--static`)],
      key: "_staticNode",
      value: void 0
    }, {
      kind: "field",
      decorators: [state()],
      key: "_hasCopy",
      value() {
        return false;
      }
    }, {
      kind: "get",
      key: "_shouldUseParentLink",
      value:
      /**
       * The non-link container node, used when the link of parent `<c4d-card>` should be used.
       */

      /**
       * `true` if there is copy content.
       */

      /**
       * `true` if the link of parent `<c4d-card>` should be used.
       */
      function () {
        const {
          href,
          parentHref
        } = this;
        return Boolean(parentHref) && (!href || parentHref === href);
      }

      /**
       * Handles `slotchange` event on the default `<slot>`.
       */
    }, {
      kind: "method",
      key: "_handleSlotChange",
      value: function _handleSlotChange({
        target
      }) {
        if (!target.name) {
          const hasContent = target.assignedNodes().some(node => node.nodeType !== Node.TEXT_NODE || node.textContent.trim());
          this._hasCopy = hasContent;
        }
      }

      /**
       * @returns The main content.
       */
    }, {
      kind: "method",
      key: "_renderContent",
      value: function _renderContent() {
        const {
          _hasCopy: hasCopy
        } = this;
        return html(_t || (_t = _`
      <span
        ?hidden="${0}"
        class="${0}--card__cta__copy"
        part="copy">
        <slot @slotchange="${0}"></slot>
      </span>
    `), !hasCopy, prefix, this._handleSlotChange);
      }
    }, {
      kind: "method",
      key: "_renderInner",
      value: function _renderInner() {
        return html(_t2 || (_t2 = _`
      ${0} ${0}
    `), this._renderContent(), this.disabled ? Error20() : this._renderIcon());
      }

      /**
       * Alternative aria label if no text in footer.
       */
    }, {
      kind: "field",
      decorators: [property({
        reflect: false
      })],
      key: "altAriaLabel",
      value: void 0
    }, {
      kind: "field",
      decorators: [property({
        attribute: 'color-scheme',
        reflect: true
      })],
      key: "colorScheme",
      value() {
        return '';
      }
    }, {
      kind: "field",
      decorators: [property({
        attribute: 'parent-href',
        reflect: true
      })],
      key: "parentHref",
      value: void 0
    }, {
      kind: "field",
      decorators: [property({
        type: Boolean
      })],
      key: "iconInline",
      value() {
        return true;
      }
    }, {
      kind: "field",
      decorators: [property({
        reflect: true
      })],
      key: "slot",
      value() {
        return 'footer';
      }
    }, {
      kind: "method",
      key: "updated",
      value:
      /**
       * The color scheme.
       */
      /**
       * The `href` in parent `<c4d-card>`.
       * `<c4d-card>` sets this automatically.
       */
      /**
       * Positions the icon inline with text when `true`
       */
      /**
       * The slot in parent `<c4d-card>`.
       */
      function updated(changedProperties) {
        _superPropGet(C4DCardFooter, "updated", this, 3)([changedProperties]);
        if (!this._hasCopy) {
          var _this$shadowRoot;
          (_this$shadowRoot = this.shadowRoot) === null || _this$shadowRoot === void 0 || (_this$shadowRoot = _this$shadowRoot.querySelector(`a`)) === null || _this$shadowRoot === void 0 || _this$shadowRoot.setAttribute('aria-label', this.altAriaLabel ? this.altAriaLabel : '');
        } else {
          var _this$shadowRoot2;
          (_this$shadowRoot2 = this.shadowRoot) === null || _this$shadowRoot2 === void 0 || (_this$shadowRoot2 = _this$shadowRoot2.querySelector(`a`)) === null || _this$shadowRoot2 === void 0 || _this$shadowRoot2.removeAttribute('aria-label');
        }
        const {
          iconInline,
          _staticNode: staticNode,
          _linkNode: linkNode
        } = this;
        const targetNode = linkNode !== null && linkNode !== void 0 ? linkNode : staticNode;
        targetNode.classList.add(`${prefix}--card__footer`);
        targetNode.classList.add(`${c4dPrefix}-ce--card__footer`);
        if (iconInline) {
          targetNode.classList.add(`${prefix}--link-with-icon--inline-icon`);
        }
      }
    }, {
      kind: "get",
      static: true,
      key: "stableSelector",
      value: function () {
        return `${c4dPrefix}--card-footer`;
      }
    }, {
      kind: "field",
      static: true,
      key: "styles",
      value() {
        return styles;
      }
    }]
  };
}, C4DLinkWithIcon);
/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DCardFooter;
//# sourceMappingURL=card-footer.js.map
